import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../utils/axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft, Download, Plus, Trash2, GripVertical, FileText, X,
  ChevronUp, ChevronDown, Loader2,
} from 'lucide-react';

// Pantalla de edición del Informe Consolidado.
// Recibe los case_ids por location.state. Permite editar títulos, resumen,
// conclusiones, mostrar/ocultar secciones y editar la tabla resumen
// (quitar/reordenar/renombrar columnas, eliminar/agregar filas, editar celdas).
function LabeledInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-500">{label}</label>
      <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="mt-0.5 h-8 text-xs" data-testid={testid} />
    </div>
  );
}

export default function InformeEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const caseIds = location.state?.caseIds || [];

  const [loading, setLoading] = useState(true);
  const [descargando, setDescargando] = useState(false);
  const [data, setData] = useState(null);

  const [titulo, setTitulo] = useState('');
  const [resumen, setResumen] = useState('');
  const [conclusiones, setConclusiones] = useState('');
  const [secciones, setSecciones] = useState({
    incluir_kpis: true,
    incluir_graficos: true,
    incluir_grafico_severidad: true,
    incluir_grafico_casos: true,
    incluir_tabla_resumen: true,
    incluir_detalle: true,
    incluir_alertas: true,
    incluir_montos: true,
  });
  const [columnas, setColumnas] = useState([]); // [{key, header, align}]
  const [filas, setFilas] = useState([]); // [{case_id?, [key]: value}]
  const [kpis, setKpis] = useState([]); // [{label, value}]
  const [detalle, setDetalle] = useState([]); // [{cliente,cuenta,cvu,cuit,estado,...,alertas:[]}]
  const [detalleCampos, setDetalleCampos] = useState({}); // {key: bool}
  const [detalleCamposList, setDetalleCamposList] = useState([]); // [{key,label}]
  const [alertaCols, setAlertaCols] = useState({ tipo: true, severidad: true, operaciones: true, monto: true, fecha: true });
  const [detalleAbierto, setDetalleAbierto] = useState({}); // {idx: bool}

  const cargar = useCallback(async () => {
    if (!caseIds.length) {
      toast.error('No se seleccionaron casos');
      navigate('/m/gestion-de-casos/casos');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/informes/multi-caso/preview', { case_ids: caseIds });
      const d = res.data;
      setData(d);
      setTitulo(d.titulo_default || '');
      setColumnas((d.columnas_default || []).map((c) => ({ ...c })));
      setFilas((d.rows || []).map((r) => ({ ...r })));
      setKpis((d.kpis || []).map((k) => ({ ...k })));
      setDetalle((d.detalle || []).map((x) => ({ ...x, alertas: (x.alertas || []).map((a) => ({ ...a })) })));
      const campos = d.detalle_campos || [];
      setDetalleCamposList(campos);
      const cobj = {};
      campos.forEach((c) => { cobj[c.key] = true; });
      setDetalleCampos(cobj);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar el informe');
    } finally {
      setLoading(false);
    }
  }, [caseIds, navigate]);

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  // ---- Columnas ----
  const toggleColAlign = (idx) => {
    setColumnas((cols) => cols.map((c, i) => i === idx ? { ...c, align: c.align === 'RIGHT' ? 'LEFT' : 'RIGHT' } : c));
  };
  const renameCol = (idx, header) => {
    setColumnas((cols) => cols.map((c, i) => i === idx ? { ...c, header } : c));
  };
  const removeCol = (idx) => {
    setColumnas((cols) => cols.filter((_, i) => i !== idx));
  };
  const moveCol = (idx, dir) => {
    setColumnas((cols) => {
      const j = idx + dir;
      if (j < 0 || j >= cols.length) return cols;
      const next = [...cols];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };
  const addCol = () => {
    const key = `extra_${Date.now()}`;
    setColumnas((cols) => [...cols, { key, header: 'Nueva columna', align: 'LEFT' }]);
  };

  // ---- Filas ----
  const editCell = (rowIdx, key, value) => {
    setFilas((rows) => rows.map((r, i) => i === rowIdx ? { ...r, [key]: value } : r));
  };
  const removeRow = (rowIdx) => {
    const removed = filas[rowIdx];
    const cid = removed?.case_id;
    setFilas((rows) => rows.filter((_, i) => i !== rowIdx));
    // Al quitar una línea del resumen, se quita su detalle automáticamente
    if (cid) {
      setDetalle((det) => det.filter((d) => d.case_id !== cid));
      setDetalleAbierto({});
    }
  };
  const addRow = () => {
    const empty = {};
    columnas.forEach((c) => { empty[c.key] = ''; });
    setFilas((rows) => [...rows, empty]);
  };

  // ---- KPIs ----
  const editKpi = (idx, field, value) => setKpis((ks) => ks.map((k, i) => i === idx ? { ...k, [field]: value } : k));
  const removeKpi = (idx) => setKpis((ks) => ks.filter((_, i) => i !== idx));
  const addKpi = () => setKpis((ks) => [...ks, { label: 'Nuevo KPI', value: '' }]);

  // ---- Detalle por caso ----
  const editDetalleCampo = (idx, key, value) => setDetalle((ds) => ds.map((d, i) => i === idx ? { ...d, [key]: value } : d));
  const removeDetalleCaso = (idx) => setDetalle((ds) => ds.filter((_, i) => i !== idx));
  const editDetalleAlerta = (di, ai, key, value) => setDetalle((ds) => ds.map((d, i) => {
    if (i !== di) return d;
    return { ...d, alertas: d.alertas.map((a, j) => j === ai ? { ...a, [key]: value } : a) };
  }));
  const removeDetalleAlerta = (di, ai) => setDetalle((ds) => ds.map((d, i) => i === di ? { ...d, alertas: d.alertas.filter((_, j) => j !== ai) } : d));
  const addDetalleAlerta = (di) => setDetalle((ds) => ds.map((d, i) => i === di ? { ...d, alertas: [...(d.alertas || []), { tipo: '', severidad: 'MEDIA', operaciones: '', monto: '', fecha: '' }] } : d));

  const descargar = async () => {
    setDescargando(true);
    try {
      // case_ids = filas que conservan case_id (KPIs/detalle reflejan esos casos)
      const idsRestantes = filas.map((f) => f.case_id).filter(Boolean);
      const campos_ocultos = Object.keys(detalleCampos).filter((k) => !detalleCampos[k]);
      const payload = {
        case_ids: idsRestantes.length ? idsRestantes : caseIds,
        titulo: titulo || null,
        resumen_ejecutivo: resumen || null,
        conclusiones: conclusiones || null,
        ...secciones,
        kpis_editados: kpis.map((k) => ({ label: k.label, value: String(k.value ?? '') })),
        columnas: columnas.map((c) => ({ key: c.key, header: c.header, align: c.align || 'LEFT' })),
        resumen_rows_editadas: filas.map((f) => {
          const obj = {};
          columnas.forEach((c) => { obj[c.key] = f[c.key] ?? ''; });
          return obj;
        }),
        detalle_campos_ocultos: campos_ocultos,
        detalle_alerta_columnas_ocultas: Object.keys(alertaCols).filter((k) => !alertaCols[k]),
        detalle_editado: secciones.incluir_detalle ? detalle.map((d) => ({
          cliente: d.cliente, cuenta: d.cuenta, cvu: d.cvu, cuit: d.cuit, estado: d.estado,
          prioridad: d.prioridad, fecha: d.fecha, asignado: d.asignado, bloqueada: d.bloqueada,
          descripcion: d.descripcion,
          alertas: (d.alertas || []).map((a) => ({ ...a })),
        })) : null,
      };
      const response = await api.post('/informes/multi-caso/pdf', payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Informe_${(idsRestantes.length || caseIds.length)}_casos_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Informe PDF generado');
    } catch (e) {
      toast.error('Error al generar el PDF');
    } finally {
      setDescargando(false);
    }
  };

  const SEC_LABELS = [
    { key: 'incluir_kpis', label: 'Indicadores (KPIs)' },
    { key: 'incluir_graficos', label: 'Gráficos' },
    { key: 'incluir_tabla_resumen', label: 'Tabla resumen' },
    { key: 'incluir_detalle', label: 'Detalle por caso' },
    { key: 'incluir_alertas', label: 'Alertas (en detalle)' },
    { key: 'incluir_montos', label: 'Montos' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-slate-500" data-testid="informe-editor-loading">
        <Loader2 className="animate-spin mr-2" /> Cargando informe…
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto pb-24" data-testid="informe-editor-page">
      {/* Header */}
      <div className="flex items-center justify-between bg-white border-b py-3 px-1 mb-5">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/m/gestion-de-casos/casos')} data-testid="informe-editor-back">
            <ArrowLeft size={16} className="mr-1" /> Volver
          </Button>
          <div className="flex items-center gap-2 text-slate-800">
            <FileText size={18} className="text-teal-600" />
            <span className="font-semibold">Editor de Informe — {data?.casos_count || caseIds.length} caso(s)</span>
          </div>
        </div>
        <Button onClick={descargar} disabled={descargando} className="bg-teal-600 hover:bg-teal-700" data-testid="informe-editor-descargar">
          {descargando ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Download size={16} className="mr-1.5" />}
          {descargando ? 'Generando…' : 'Descargar PDF'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 px-1">
        {/* Panel lateral: secciones */}
        <div className="lg:col-span-1 space-y-4">
          <div className="border rounded-lg p-4 bg-slate-50">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Secciones del informe</h3>
            <div className="space-y-2">
              {SEC_LABELS.map((s) => (
                <div key={s.key}>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <Checkbox
                      checked={secciones[s.key]}
                      onCheckedChange={(v) => setSecciones((p) => ({ ...p, [s.key]: !!v }))}
                      data-testid={`editor-seccion-${s.key}`}
                    />
                    {s.label}
                  </label>
                  {s.key === 'incluir_graficos' && secciones.incluir_graficos && (
                    <div className="ml-6 mt-1 space-y-1">
                      <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                        <Checkbox checked={secciones.incluir_grafico_severidad}
                          onCheckedChange={(v) => setSecciones((p) => ({ ...p, incluir_grafico_severidad: !!v }))}
                          data-testid="editor-seccion-incluir_grafico_severidad" />
                        Gráfico de severidad
                      </label>
                      <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                        <Checkbox checked={secciones.incluir_grafico_casos}
                          onCheckedChange={(v) => setSecciones((p) => ({ ...p, incluir_grafico_casos: !!v }))}
                          data-testid="editor-seccion-incluir_grafico_casos" />
                        Gráfico por cliente
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Campos del detalle por caso */}
          {secciones.incluir_detalle && detalleCamposList.length > 0 && (
            <div className="border rounded-lg p-4 bg-slate-50">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Campos del detalle</h3>
              <div className="space-y-2">
                {detalleCamposList.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <Checkbox
                      checked={!!detalleCampos[c.key]}
                      onCheckedChange={(v) => setDetalleCampos((p) => ({ ...p, [c.key]: !!v }))}
                      data-testid={`editor-detalle-campo-${c.key}`}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Columnas de la tabla de alertas */}
          {secciones.incluir_detalle && secciones.incluir_alertas && (
            <div className="border rounded-lg p-4 bg-slate-50">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Columnas de alertas</h3>
              <div className="space-y-2">
                {[
                  { key: 'tipo', label: 'Tipo' },
                  { key: 'severidad', label: 'Severidad' },
                  { key: 'operaciones', label: 'Ops' },
                  { key: 'monto', label: 'Monto' },
                  { key: 'fecha', label: 'Fecha' },
                ].map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <Checkbox
                      checked={!!alertaCols[c.key]}
                      onCheckedChange={(v) => setAlertaCols((p) => ({ ...p, [c.key]: !!v }))}
                      data-testid={`editor-alerta-col-${c.key}`}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {secciones.incluir_kpis && (
            <div className="border rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-700">KPIs (editables)</h3>
                <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={addKpi} data-testid="editor-add-kpi"><Plus size={11} className="mr-1" />KPI</Button>
              </div>
              <div className="space-y-2">
                {kpis.map((k, i) => (
                  <div key={i} className="flex items-center gap-1" data-testid={`editor-kpi-${i}`}>
                    <Input value={k.value} onChange={(e) => editKpi(i, 'value', e.target.value)} className="h-8 text-sm font-bold w-20 text-center" data-testid={`editor-kpi-value-${i}`} />
                    <Input value={k.label} onChange={(e) => editKpi(i, 'label', e.target.value)} className="h-8 text-xs flex-1" data-testid={`editor-kpi-label-${i}`} />
                    <button onClick={() => removeKpi(i)} className="text-red-400 hover:text-red-600" title="Quitar KPI" data-testid={`editor-kpi-remove-${i}`}><X size={14} /></button>
                  </div>
                ))}
                {kpis.length === 0 && <p className="text-xs text-slate-400">Sin KPIs. Agregá uno.</p>}
              </div>
            </div>
          )}
        </div>

        {/* Panel principal: textos + tabla */}
        <div className="lg:col-span-3 space-y-5">
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Título</label>
              <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} className="mt-1" data-testid="editor-titulo" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Resumen ejecutivo</label>
              <Textarea value={resumen} onChange={(e) => setResumen(e.target.value)} rows={3} className="mt-1 text-sm" placeholder="Texto introductorio del informe…" data-testid="editor-resumen" />
            </div>
          </div>

          {/* Tabla resumen editable */}
          <div className="border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between bg-slate-800 text-white px-4 py-2">
              <span className="text-sm font-semibold">Resumen de Casos (editable)</span>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={addCol} data-testid="editor-add-col"><Plus size={12} className="mr-1" /> Columna</Button>
                <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={addRow} data-testid="editor-add-row"><Plus size={12} className="mr-1" /> Fila</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="editor-tabla">
                <thead>
                  <tr className="bg-slate-100">
                    {columnas.map((c, ci) => (
                      <th key={c.key} className="p-2 border-b border-r text-left align-top min-w-[140px]">
                        <div className="flex items-center gap-1 mb-1">
                          <button onClick={() => moveCol(ci, -1)} className="text-slate-400 hover:text-slate-700" title="Mover izquierda" data-testid={`editor-col-left-${ci}`}><ChevronUp size={12} className="-rotate-90" /></button>
                          <button onClick={() => moveCol(ci, 1)} className="text-slate-400 hover:text-slate-700" title="Mover derecha" data-testid={`editor-col-right-${ci}`}><ChevronDown size={12} className="-rotate-90" /></button>
                          <button onClick={() => toggleColAlign(ci)} className="text-[10px] px-1 rounded bg-slate-200 text-slate-600 hover:bg-slate-300" title="Alinear">{c.align === 'RIGHT' ? '→' : '←'}</button>
                          <button onClick={() => removeCol(ci)} className="ml-auto text-red-400 hover:text-red-600" title="Quitar columna" data-testid={`editor-col-remove-${ci}`}><X size={13} /></button>
                        </div>
                        <Input value={c.header} onChange={(e) => renameCol(ci, e.target.value)} className="h-7 text-xs font-semibold" data-testid={`editor-col-header-${ci}`} />
                      </th>
                    ))}
                    <th className="p-2 border-b w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f, ri) => (
                    <tr key={ri} className="hover:bg-slate-50">
                      {columnas.map((c) => (
                        <td key={c.key} className="p-1 border-b border-r">
                          <Input
                            value={f[c.key] ?? ''}
                            onChange={(e) => editCell(ri, c.key, e.target.value)}
                            className="h-8 text-xs border-transparent hover:border-slate-200 focus:border-teal-400"
                            data-testid={`editor-cell-${ri}-${c.key}`}
                          />
                        </td>
                      ))}
                      <td className="p-1 border-b text-center">
                        <button onClick={() => removeRow(ri)} className="text-red-400 hover:text-red-600" title="Eliminar fila" data-testid={`editor-row-remove-${ri}`}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                  {filas.length === 0 && (
                    <tr><td colSpan={columnas.length + 1} className="p-4 text-center text-slate-400 text-sm">Sin filas. Agregá una con "Fila".</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detalle por caso editable */}
          {secciones.incluir_detalle && (
            <div className="border rounded-lg overflow-hidden" data-testid="editor-detalle-section">
              <div className="bg-slate-800 text-white px-4 py-2 text-sm font-semibold">Detalle por Caso (editable)</div>
              <div className="divide-y">
                {detalle.map((d, di) => {
                  const abierto = detalleAbierto[di];
                  return (
                    <div key={di} className="bg-white" data-testid={`editor-detalle-caso-${di}`}>
                      <div className="flex items-center justify-between px-3 py-2 hover:bg-slate-50">
                        <button className="flex items-center gap-2 text-sm font-medium text-slate-700 flex-1 text-left"
                          onClick={() => setDetalleAbierto((p) => ({ ...p, [di]: !p[di] }))}
                          data-testid={`editor-detalle-toggle-${di}`}>
                          {abierto ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          Caso {di + 1} — {d.cliente || 'Sin nombre'}
                          <span className="text-xs text-slate-400">({(d.alertas || []).length} alertas)</span>
                        </button>
                        <button onClick={() => removeDetalleCaso(di)} className="text-red-400 hover:text-red-600 ml-2" title="Quitar caso del detalle" data-testid={`editor-detalle-remove-${di}`}><Trash2 size={14} /></button>
                      </div>
                      {abierto && (
                        <div className="px-3 pb-4 space-y-3">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            <LabeledInput label="Cliente" value={d.cliente} onChange={(v) => editDetalleCampo(di, 'cliente', v)} testid={`editor-detalle-${di}-cliente`} />
                            {detalleCamposList.filter((c) => detalleCampos[c.key]).map((c) => (
                              <LabeledInput key={c.key} label={c.label} value={d[c.key]} onChange={(v) => editDetalleCampo(di, c.key, v)} testid={`editor-detalle-${di}-${c.key}`} />
                            ))}
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-500">Descripción</label>
                            <Textarea value={d.descripcion || ''} onChange={(e) => editDetalleCampo(di, 'descripcion', e.target.value)} rows={2} className="mt-1 text-xs" data-testid={`editor-detalle-${di}-descripcion`} />
                          </div>
                          {secciones.incluir_alertas && (
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-semibold text-slate-600">Alertas detectadas</span>
                                <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => addDetalleAlerta(di)} data-testid={`editor-detalle-${di}-add-alerta`}><Plus size={11} className="mr-1" />Alerta</Button>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs border">
                                  <thead><tr className="bg-slate-100 text-slate-600">
                                    {alertaCols.tipo && <th className="p-1 border text-left">Tipo</th>}
                                    {alertaCols.severidad && <th className="p-1 border">Severidad</th>}
                                    {alertaCols.operaciones && <th className="p-1 border">Ops</th>}
                                    {alertaCols.monto && <th className="p-1 border">Monto</th>}
                                    {alertaCols.fecha && <th className="p-1 border">Fecha</th>}
                                    <th className="p-1 border w-8"></th>
                                  </tr></thead>
                                  <tbody>
                                    {(d.alertas || []).map((a, ai) => (
                                      <tr key={ai}>
                                        {alertaCols.tipo && <td className="border p-0.5"><Input value={a.tipo || ''} onChange={(e) => editDetalleAlerta(di, ai, 'tipo', e.target.value)} className="h-7 text-xs border-transparent" data-testid={`editor-detalle-${di}-alerta-${ai}-tipo`} /></td>}
                                        {alertaCols.severidad && <td className="border p-0.5"><Input value={a.severidad || ''} onChange={(e) => editDetalleAlerta(di, ai, 'severidad', e.target.value)} className="h-7 text-xs border-transparent w-24" /></td>}
                                        {alertaCols.operaciones && <td className="border p-0.5"><Input value={a.operaciones || ''} onChange={(e) => editDetalleAlerta(di, ai, 'operaciones', e.target.value)} className="h-7 text-xs border-transparent w-16" /></td>}
                                        {alertaCols.monto && <td className="border p-0.5"><Input value={a.monto || ''} onChange={(e) => editDetalleAlerta(di, ai, 'monto', e.target.value)} className="h-7 text-xs border-transparent w-24" /></td>}
                                        {alertaCols.fecha && <td className="border p-0.5"><Input value={a.fecha || ''} onChange={(e) => editDetalleAlerta(di, ai, 'fecha', e.target.value)} className="h-7 text-xs border-transparent w-24" /></td>}
                                        <td className="border p-0.5 text-center"><button onClick={() => removeDetalleAlerta(di, ai)} className="text-red-400 hover:text-red-600"><X size={12} /></button></td>
                                      </tr>
                                    ))}
                                    {(d.alertas || []).length === 0 && <tr><td colSpan={6} className="p-2 text-center text-slate-400">Sin alertas</td></tr>}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {detalle.length === 0 && <p className="p-4 text-center text-slate-400 text-sm">Sin casos en el detalle.</p>}
              </div>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-slate-700">Conclusiones / Notas</label>
            <Textarea value={conclusiones} onChange={(e) => setConclusiones(e.target.value)} rows={4} className="mt-1 text-sm" placeholder="Observaciones, conclusiones o notas a incluir al final del informe…" data-testid="editor-conclusiones" />
          </div>
        </div>
      </div>
    </div>
  );
}
